/* eslint-disable camelcase */
import React, {
    useEffect, useMemo, useState, useRef,
    MutableRefObject,
} from 'react';

import { analyticService } from 'global/services';
import { FrameDialog } from 'app/components/Dialog';
import { UIButton, UIRadioIcon } from 'app/components/ui';
import { Portal } from 'react-portal';
import cn from 'classnames';
import { DialogStaticDesktop } from 'app/components/Dialog/BR1156/DialogStaticDesktop';
import { DialogAnimationWrapper } from 'app/components/Dialog/DialogAnimationWrapper';
import { UIRadioIconStatic } from 'app/components/ui/UIRadioIcon/UIRadioIconStatic';
import { Desktop, Mobile } from 'app/components/Responsive';
import { gql, useApolloClient, useReactiveVar } from '@apollo/client';
import { CATEGORIES_URL_VALUES } from 'app/const/categories';
import { isGlobalLoadingVar } from 'app/apollo/reaction';
import Icon from './img/arrow.component.svg';
import Icon2 from './img/arrow2.component.svg';
import Icon3 from './img/arrow3.component.svg';

import css from './mobileCustomIngredientsSelect.module.scss';
import { ingredientTypeTitleRecord } from '../customIngredientsSelectDishRow.records';
// import { NextOverlay } from './overlay/nextOverlay';
import { ICustomIngredientsSelectProps } from '../CustomIngredientsSelectDishRow.types';

interface ISelectComponentProps extends ICustomIngredientsSelectProps {
    isOpen: boolean
    handleSubmit: () => void
    handleClose: () => void
}

const DialogWrapper = (props: any) => {
    const { children, handleClose, handleSubmit } = props;

    return (
        <>
            <Mobile>
                {/* @ts-ignore */}
                <FrameDialog
                    handleClose={handleClose}
                >
                    {children}
                </FrameDialog>
            </Mobile>
            <Desktop>
                <DialogStaticDesktop
                    onlyConfirmButton
                    onConfirm={handleSubmit}
                    onReject={handleClose}
                    confirmText="Готово"
                    isCloseConfirmAction={false}
                >
                    {children}
                </DialogStaticDesktop>
            </Desktop>
        </>
    );
};

const SelectComponent = (props: ISelectComponentProps) => {
    const {
        id,
        isOpen,
        handleClose,
        selectedDishId,
        secondDishPart,
        optionsList = [],
        checkIsSelected,
        onNextIdSelected,
        handleSubmit = () => { },
        titleRenderField,
    } = props;

    const previousSelectedId: MutableRefObject<string | null> = useRef<string | null>(null);
    const source_name = window?.location?.pathname?.includes('menu') ? 'menu' : 'basket';

    const handleClickOnIngredientItem = (targetId: string) => {
        onNextIdSelected(targetId);
        analyticService.push({
            eventName: 'Custom_Card_Change',
            dish_id: targetId,
            source_name,
        });
    };

    useEffect(() => {
        if (isOpen) {
            analyticService.push({
                eventName: 'Custom_Card_Open',
                dish_id: id,
                source_name,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    /* SELECTORS */
    const title = useMemo(
        () => (titleRenderField === 'replaceableIngredient'
            // @ts-ignore
            ? ingredientTypeTitleRecord[secondDishPart]
            : ingredientTypeTitleRecord[3]
        ) || '',
        [secondDishPart, titleRenderField],
    );

    return (
        // @ts-ignore
        <DialogWrapper
            handleSubmit={handleSubmit}
            handleClose={() => {
                const prevId: string | null = previousSelectedId.current;
                if (prevId) {
                    handleClickOnIngredientItem(prevId);
                }
                handleClose();
            }}
        >
            <div className={css.selectIngredientsRoot}>
                <div className={css.selectIngredientsTitle}>
                    Выберите
                    {' '}
                    {title}
                </div>
                <div className={css.selectIngredientsItems}>
                    {optionsList.map((item: any) => (
                        <button
                            className={cn(css.selectIngredientsItemButton)}
                            type="button"
                            onClick={() => {
                                previousSelectedId.current = id;
                                handleClickOnIngredientItem(item.id);
                            }}
                            id={item.id}
                            key={item.id}
                            disabled={selectedDishId === item.id}
                        >
                            <div
                                className={
                                    // @ts-ignore
                                    cn(css.selectIngredientsItemText, checkIsSelected(item.id, selectedDishId))
                                }
                            >
                                {item[titleRenderField] ? item[titleRenderField] : item?.title}
                            </div>
                            <Mobile>
                                <UIRadioIcon checked={item.id === selectedDishId} />
                            </Mobile>
                            <Desktop>
                                <UIRadioIconStatic checked={item.id === selectedDishId} />
                            </Desktop>
                        </button>
                    ))}
                </div>
                <Mobile>
                    <div className={css.selectIngredientsSubmit}>
                        <UIButton onClick={handleSubmit}>
                            Готово
                        </UIButton>
                    </div>
                </Mobile>
            </div>
        </DialogWrapper>
    );
};

// NOTE: Используется в корзинах в desktop'е
export function MobileCustomIngredientsSelect(props: ICustomIngredientsSelectProps) {
    const {
        id,
        selectedDishId,
        ingredientTypeText,
        replaceableIngredient,
        checkIsSelected,
        optionsList,
        secondDishPart,
        onNextIdSelected,
        environmentContext,
        onSubmitHandler = () => { },
        disabled = false,
        isNewLayout,
        titleRenderField,
    } = props;

    const client = useApolloClient();
    const isGlobalLoading = useReactiveVar(isGlobalLoadingVar);

    const { category_id } = client.readFragment({
        id: `menuDish:${selectedDishId}`,
        fragment: gql`
            fragment DISH_CATEGORY_ANALYTIC_FRAGMENT on menuDish {
                category_id,
            }`,
    }) || { category_id: 3 };

    const [isOpen, setIsOpen] = useState(false);
    const source_name = window?.location?.pathname?.includes('menu') ? 'menu' : 'basket';

    const titleShown = useMemo(
        () => {
            const selectedDish = optionsList.find((item: any) => item.id === selectedDishId);
            const selectedDishTitle = selectedDish?.name_upsell || selectedDish?.title;
            return titleRenderField === 'replaceableIngredient'
                ? replaceableIngredient
                : selectedDishTitle;
        },
        [optionsList, replaceableIngredient, titleRenderField, selectedDishId],
    );

    const openSelect = () => {
        analyticService.push({
            eventName: 'Custom_Card_Click',
            dish_id: id,
            source_name,
        });

        // @ts-ignore
        setIsOpen(true);
    };

    const handleClose = () => {
        analyticService.push({
            eventName: 'Custom_Card_Close',
            dish_id: id,
            source_name,
        });
        setIsOpen(false);
    };

    const handleSubmit = () => {
        analyticService.push({
            eventName: 'Custom_Card_Close_Confirmation',
            dish_id: id,
            source_name,
            // @ts-expect-error
            dish_category: CATEGORIES_URL_VALUES[category_id],
        });
        onSubmitHandler({
            onCompoleted: () => {
                setIsOpen(false);
            },
        });
    };

    const customIngredientsSelect = cn({
        [css.customIngredientsSelectRoot]: true,
        [css.basket]: environmentContext === 'basket',
        [css.menu]: environmentContext === 'menu',
        [css.newLayout]: isNewLayout,
    });

    const titleClasses = cn({
        [css.customIngredientsSelectButton]: true,
        [css.basket]: environmentContext === 'basket',
        [css.menu]: environmentContext === 'menu',
        [css.newLayout]: isNewLayout,
    });

    const iconClasses = cn({
        [css.customIngredientsSelectIcon]: true,
        [css.basket]: environmentContext === 'basket',
        [css.menu]: environmentContext === 'menu',
        [css.newLayout]: isNewLayout,
    });

    const customIngredientsSelectTextClasses = cn({
        [css.customIngredientsSelectText]: true,
        [css.newLayout]: isNewLayout,
        [css.basket]: environmentContext === 'basket',
        [css.menu]: environmentContext === 'menu',
        [css.lp]: environmentContext === 'lp',
    });

    const ingredientTypeClasses = cn({
        [css.ingredientType]: true,
        [css.newLayout]: isNewLayout,
    });

    return (
        <div className={customIngredientsSelect}>
            <button
                type="button"
                onClickCapture={(e) => {
                    e.stopPropagation();
                    openSelect();
                }}
                className={titleClasses}
                disabled={disabled || isGlobalLoading}
            >
                <div className={customIngredientsSelectTextClasses}>
                    <span
                        className={`${ingredientTypeClasses} ${isNewLayout && 'body-s-medium'}`}
                        style={{
                            textTransform: isNewLayout
                                ? 'lowercase'
                                : undefined,
                        }}
                    >
                        {ingredientTypeText}
                    </span>
                    <span
                        className={`${css.clampText} ${isNewLayout && 'body-s-medium'}`}
                        style={{
                            textTransform: isNewLayout
                                ? 'lowercase'
                                : undefined,
                        }}
                    >
                        {titleShown}
                    </span>
                </div>
                <div className={iconClasses}>
                    {/* <Icon /> */}
                    {/* <Icon2 /> */}
                    <Icon3 />
                </div>
            </button>
            <Portal>
                <DialogAnimationWrapper
                    isOpen={isOpen}
                    onReject={handleClose}
                >
                    <SelectComponent
                        id={id}
                        isOpen={isOpen}
                        handleClose={handleClose}
                        selectedDishId={selectedDishId}
                        secondDishPart={secondDishPart}
                        checkIsSelected={checkIsSelected}
                        optionsList={optionsList}
                        onNextIdSelected={onNextIdSelected}
                        handleSubmit={handleSubmit}
                        titleRenderField={titleRenderField}
                    />
                </DialogAnimationWrapper>
            </Portal>
        </div>

    );
}
